import React, { useState, useEffect, useMemo } from 'react'

import {
    Popover,
    Paper,
    Typography,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
} from '@material-ui/core'

import {
    BaseWorkorder,
    Area,
    AreaConfig,
    ModelMap,
    AreaStatusTag,
} from '../../../../models'
import { UnitRes } from '../../../../store'

interface ServiceAreaUIState {
    [areaId: number]: {
        include: boolean
    }
}

interface ServiceAreaEditPopoverProps {
    open: boolean
    anchorEl: HTMLElement | null
    workorder: BaseWorkorder | null
    areaConfigMap: ModelMap<AreaConfig>
    unitMap: Record<number, UnitRes> | undefined
    areaStatusTagMap: ModelMap<AreaStatusTag>
    onClose: () => void
    onServiceAreaChange: (
        workorder: BaseWorkorder,
        serviceAreas: number[],
    ) => void
}

const ServiceArea = (props: {
    area: Area
    areaConfig?: AreaConfig
    checked: boolean
    areaStatusTag?: AreaStatusTag
    onChange: () => void
}) => {
    const { area, checked, onChange, areaConfig, areaStatusTag } = props
    return (
        <FormControlLabel
            control={
                <Checkbox
                    color="primary"
                    checked={checked}
                    onChange={onChange}
                />
            }
            label={
                <div>
                    <span>
                        {areaConfig?.name} {area.area_label}{' '}
                        {areaStatusTag
                            ? `(${areaStatusTag.area_status_config.name})`
                            : ''}
                    </span>
                </div>
            }
        />
    )
}

export const ServiceAreaEditPopover = ({
    open,
    anchorEl,
    workorder,
    areaConfigMap,
    unitMap,
    areaStatusTagMap,
    onServiceAreaChange,
    onClose,
}: ServiceAreaEditPopoverProps) => {
    const [
        serviceAreaUIState,
        setServiceAreaUIState,
    ] = useState<ServiceAreaUIState>({})

    const unitAreas = useMemo(() => {
        return workorder?.unit_id && unitMap
            ? unitMap[workorder.unit_id]?.areas.filter(
                  (area) => areaConfigMap[area.area_config]?.shows_on_schedule,
              )
            : []
    }, [workorder, unitMap, areaConfigMap])

    // Initialize form values when workorder changes
    useEffect(() => {
        if (workorder && unitAreas) {
            const newSaUiState: ServiceAreaUIState = {}
            unitAreas.forEach((area) => {
                newSaUiState[area.id] = {
                    include: false,
                }
            })
            workorder.service_area_list.forEach((sa) => {
                newSaUiState[sa.area_id] = {
                    include: true,
                }
            })
            setServiceAreaUIState(newSaUiState)
        }
    }, [workorder, unitAreas])

    const handleSave = () => {
        if (workorder) {
            const serviceAreas = Object.keys(serviceAreaUIState).reduce<
                number[]
            >((prev, areaId) => {
                const aid = parseInt(areaId)
                if (serviceAreaUIState[aid].include) {
                    return prev.concat(aid)
                }
                return prev
            }, [])

            onServiceAreaChange(workorder, serviceAreas)
            onClose()
        }
    }

    const handleCancel = () => {
        // Reset to original values
        if (workorder && unitAreas) {
            const newSaUiState: ServiceAreaUIState = {}
            unitAreas.forEach((area) => {
                newSaUiState[area.id] = {
                    include: false,
                }
            })
            workorder.service_area_list.forEach((sa) => {
                newSaUiState[sa.area_id] = {
                    include: true,
                }
            })
            setServiceAreaUIState(newSaUiState)
        }
        onClose()
    }

    if (!workorder || !unitAreas || !unitMap) return null

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                style: {
                    padding: '20px',
                    minWidth: '500px',
                    maxHeight: '500px',
                    overflow: 'auto',
                },
            }}
        >
            <Paper elevation={0}>
                <Box mb={3}>
                    <Typography variant="h6" gutterBottom>
                        Edit Service Areas
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {workorder.service_name} - {workorder.unit_name}
                    </Typography>
                </Box>

                <Box mb={3}>
                    <Typography variant="subtitle2" gutterBottom>
                        Select areas to include in this workorder:
                    </Typography>

                    <FormGroup>
                        <Box
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '8px',
                                maxHeight: '300px',
                                overflow: 'auto',
                            }}
                        >
                            {unitAreas.map((area) => (
                                <ServiceArea
                                    key={`SERVICE_AREA_${area.id}`}
                                    area={area}
                                    areaConfig={areaConfigMap[area.area_config]}
                                    checked={
                                        serviceAreaUIState[area.id]?.include ===
                                        true
                                    }
                                    onChange={() =>
                                        setServiceAreaUIState({
                                            ...serviceAreaUIState,
                                            [area.id]: {
                                                include: !serviceAreaUIState[
                                                    area.id
                                                ]?.include,
                                            },
                                        })
                                    }
                                    areaStatusTag={areaStatusTagMap[area.id]}
                                />
                            ))}
                        </Box>
                    </FormGroup>
                </Box>

                <Box display="flex" justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        style={{ marginRight: '8px' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </Box>
            </Paper>
        </Popover>
    )
}
